import React from 'react';
import { Settings, LayoutDashboard, Building2, MessageSquare, History, BarChart3 } from 'lucide-react';
import PlanManagement from './PlanManagement';
import PropertiesSettingsPage from './PropertiesSettings';
import OtaOptionsManagement from './OtaOptionsManagement';

const iconMap = {
  dashboard: LayoutDashboard,
  'hotel-onboarded': Building2,
  properties: Building2,
  'hotel-lifecycle': Building2,
  broadcast: MessageSquare,
  chatbot: MessageSquare,
  whatsapp: MessageSquare,
  analytics: BarChart3,
  'audit-history': History,
  general: Settings,
};

/**
 * Generic Module Settings Placeholder
 */
const ModuleSettings = ({ moduleId, title, description }) => {
  const Icon = iconMap[moduleId] || Settings;

  return (
    <div className="space-y-6" data-testid={`${moduleId}-settings-page`}>
      <div>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
          <Icon size={28} className="text-teal-600" />
          {title} Settings
        </h1>
        <p className="text-gray-500 mt-1">{description}</p>
      </div>

      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8 text-center">
        <Icon size={48} className="text-gray-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-600 mb-2">Coming Soon</h3>
        <p className="text-gray-500">
          Settings for {title} module will be available in a future update.
        </p>
      </div>
    </div>
  );
};

// Export individual module pages
export const DashboardSettings = () => (
  <ModuleSettings 
    moduleId="dashboard" 
    title="Dashboard" 
    description="Configure dashboard widgets and layout"
  />
);

export const HotelOnboardedSettings = () => (
  <div className="space-y-6" data-testid="hotel-onboarded-settings-page">
    <div>
      <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3">
        <Building2 size={28} className="text-teal-600" />
        Accounts Settings
      </h1>
      <p className="text-gray-500 mt-1">Manage plans available in the Add Account → Commercials section and the OTA options used in Add / Edit Account → OTA Information.</p>
    </div>
    <PlanManagement />
    <OtaOptionsManagement />
  </div>
);

export const PropertiesSettings = () => <PropertiesSettingsPage />;

export const HotelLifecycleSettings = () => (
  <ModuleSettings 
    moduleId="hotel-lifecycle" 
    title="Hotel Lifecycle" 
    description="Configure lifecycle stages and workflows"
  />
);

export const BroadcastSettings = () => (
  <ModuleSettings 
    moduleId="broadcast" 
    title="Broadcast" 
    description="Configure broadcast settings and templates"
  />
);

export const ChatbotSettings = () => (
  <ModuleSettings 
    moduleId="chatbot" 
    title="Chatbot" 
    description="Configure chatbot responses and triggers"
  />
);

// WhatsAppSettings is now a full component in its own file
export { default as WhatsAppSettings } from './WhatsAppSettings';

export const AnalyticsSettings = () => (
  <ModuleSettings 
    moduleId="analytics" 
    title="Analytics" 
    description="Configure analytics and reporting settings"
  />
);

export const AuditHistorySettings = () => (
  <ModuleSettings 
    moduleId="audit-history" 
    title="Audit History" 
    description="Configure audit log settings"
  />
);

export const GeneralSettings = () => (
  <ModuleSettings 
    moduleId="general" 
    title="System" 
    description="Configure general system settings"
  />
);

export default ModuleSettings;
